'use client';

import React from 'react';
import Link from 'next/link';
import { ChevronRight, Home } from 'lucide-react';
import { useDynamicBreadcrumbs } from '@/hooks/useDynamicBreadcrumbs';
import { cn } from '@/lib/utils';

interface BreadcrumbsProps {
  className?: string;
  showHome?: boolean;
  customLabels?: Record<string, string>;
  dynamicValues?: Record<string, string>;
  maxItems?: number;
}

const EMPTY_CUSTOM_LABELS: Record<string, string> = {};
const EMPTY_DYNAMIC_VALUES: Record<string, string> = {};

export function Breadcrumbs({
  className,
  showHome = true,
  customLabels = EMPTY_CUSTOM_LABELS,
  dynamicValues = EMPTY_DYNAMIC_VALUES,
  maxItems = 5,
}: BreadcrumbsProps) {
  const { breadcrumbs, isLoading } = useDynamicBreadcrumbs({
    customLabels,
    dynamicValues,
  });

  // Limit breadcrumbs if maxItems is specified
  const displayBreadcrumbs =
    maxItems > 0 && breadcrumbs.length > maxItems
      ? [
          ...breadcrumbs.slice(0, 1), // Keep first item (Home)
          { label: '...', href: '#', isActive: false },
          ...breadcrumbs.slice(-(maxItems - 2)), // Keep last items
        ]
      : breadcrumbs;

  if (breadcrumbs.length === 0) {
    return null;
  }

  // Show loading state if data is being fetched
  if (isLoading) {
    return (
      <nav
        className={cn(
          'flex items-center space-x-1 text-sm text-ds-text-subtle',
          className
        )}
        aria-label="Breadcrumb"
      >
        <span className="animate-pulse">Loading...</span>
      </nav>
    );
  }

  return (
    <nav
      className={cn(
        'flex items-center space-x-1 text-sm text-ds-text-subtle',
        className
      )}
      aria-label="Breadcrumb"
    >
      {displayBreadcrumbs.map((breadcrumb, index) => {
        const isLast = index === displayBreadcrumbs.length - 1;
        const isHome = breadcrumb.href === '/home';
        // Check if href contains dynamic placeholders like [repoName]
        const hasInvalidHref =
          breadcrumb.href?.includes('[') || breadcrumb.href?.includes(']');

        return (
          <React.Fragment key={breadcrumb.href ?? index}>
            {index > 0 && (
              <ChevronRight
                className="h-4 w-4 text-ds-text-subtle"
                aria-hidden="true"
              />
            )}

            {isLast || hasInvalidHref ? (
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 font-medium text-ds-text-darker min-w-0 max-w-[240px]'
                )}
                aria-current={isLast ? 'page' : undefined}
                title={breadcrumb.label}
              >
                {isHome && showHome && (
                  <Home className="h-4 w-4 inline mr-1 shrink-0" />
                )}
                <span className="truncate">{breadcrumb.label}</span>
              </span>
            ) : (
              <Link
                href={breadcrumb.href || '#'}
                className={cn(
                  'hover:text-ds-text-darker transition-colors',
                  'min-w-0 max-w-[200px]',
                  'inline-flex items-center gap-1.5',
                  'font-medium',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-brand-subtle focus-visible:ring-offset-2',
                  'hover:underline underline-offset-2'
                )}
                title={breadcrumb.label}
                tabIndex={0}
                onClick={(e) => {
                  // Prevent navigation if href is invalid
                  if (!breadcrumb.href || breadcrumb.href === '#') {
                    e.preventDefault();
                  }
                }}
              >
                {isHome && showHome && (
                  <Home className="h-4 w-4 inline mr-1 shrink-0" />
                )}
                <span className="truncate">{breadcrumb.label}</span>
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// Compact version for smaller spaces
export function CompactBreadcrumbs(props: BreadcrumbsProps) {
  return (
    <Breadcrumbs
      {...props}
      maxItems={3}
      className={cn('text-xs', props.className)}
    />
  );
}

// Full breadcrumb with page title
export function BreadcrumbWithTitle({
  className,
  titleClassName,
  ...props
}: BreadcrumbsProps & {
  titleClassName?: string;
}) {
  const { pageTitle } = useDynamicBreadcrumbs({
    customLabels: props.customLabels,
    dynamicValues: props.dynamicValues,
  });

  return (
    <div className={cn('space-y-2', className)}>
      <Breadcrumbs {...props} />
      <h1
        className={cn(
          'text-2xl font-semibold text-ds-text-darker',
          titleClassName
        )}
      >
        {pageTitle}
      </h1>
    </div>
  );
}
